import { HEALTH_ID, reportHealthError } from '../analytics/appHealthEvents'
import { canonicalId, parseCanonicalId, type RouteKey } from './routeLibrary/routeKey'
import type { RouteHistoryEntry, RouteSnapshot } from './routeLibrary/routeHistoryList'

/**
 * `localStorage` backend for the route library (contract §2.5).
 *
 * Reuses `routeSelectionPersistence`'s discipline: a malformed payload or a
 * `QuotaExceededError` degrades to an empty list / a no-op and surfaces as an
 * app-health `warning`. Nothing in this module may throw into a render path —
 * storage is shared with other tabs, other app versions and the user's own
 * devtools, so every read is treated as untrusted input.
 */

/** Contract keys — identical strings on iOS, Android and Web (§2.5). */
export const HISTORY_KEY = 'metro_route_history_v1'
export const SAVED_KEY = 'metro_saved_routes_v1'

/**
 * A user-favourited route (§2.4). Uncapped by design — favouriting is an
 * explicit action, and a silent cap there is a bad surprise.
 *
 * `variantHint` and `sortOrder` from the contract are not persisted yet: web
 * has no favourites UI to produce them (spec §4 keeps UI out of scope), and
 * writing fields no surface can fill would freeze a guess into storage.
 */
export interface SavedRoute {
  readonly key: RouteKey
  readonly label: string | null
  readonly savedAtIso: string
}

/**
 * Wire shape. The key is flattened to its canonical id string rather than a
 * nested object: it is what iOS and Android persist, and it keeps a
 * hand-inspected payload readable.
 */
interface StoredHistoryRow {
  readonly id: string
  readonly lastUsedIso: string
  readonly snapshot?: RouteSnapshot
  readonly useCount?: number
}

interface StoredSavedRow {
  readonly id: string
  readonly label: string | null
  readonly savedAtIso: string
}

/**
 * Read a stored array, or `[]` for anything else — absent, unparseable, or a
 * non-array payload. Rows are NOT validated here; each caller checks the
 * fields it needs, because "which fields make a row usable" differs per key.
 */
function readRows<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key)
    if (raw === null) return []
    const parsed = JSON.parse(raw) as unknown
    return Array.isArray(parsed) ? (parsed as T[]) : []
  } catch {
    // Unparseable JSON, or localStorage unavailable (private mode, storage
    // disabled by policy). "No history" is the correct degraded reading.
    return []
  }
}

function writeRows(key: string, rows: unknown, what: string): void {
  try {
    localStorage.setItem(key, JSON.stringify(rows))
  } catch (err: unknown) {
    // Best-effort, exactly like `routeSelectionPersistence.save`: private
    // mode and quota exhaustion degrade the feature, they do not break the
    // app, and the degradation is visible in app-health rather than silent.
    reportHealthError(HEALTH_ID.persistenceWriteFailed, `${what} save failed: ${String(err)}`, {
      domain: 'persistence',
      severity: 'warning',
      fallback: 'default',
    })
  }
}

/** The stored id must still parse — a row that fails is dropped, not guessed. */
function keyFromRow(row: { readonly id?: unknown } | null | undefined): RouteKey | null {
  return typeof row?.id === 'string' ? parseCanonicalId(row.id) : null
}

/**
 * A snapshot is trusted only if every field it promises is actually there.
 *
 * `computedAtIso` is the load-bearing one: the contract's 24 h freshness TTL
 * is answerable ONLY from it, so a snapshot missing it (or carrying a
 * wrong-typed duration) must read as "no snapshot" rather than as a
 * half-populated one. A TTL check against a missing timestamp parses to NaN
 * and silently renders a stale duration as current — the exact failure the
 * TTL exists to prevent. Dropping the snapshot degrades to "route without a
 * duration", which every consumer already has to handle.
 */
function snapshotFromRow(snapshot: unknown): RouteSnapshot | null {
  if (typeof snapshot !== 'object' || snapshot === null) return null
  const s = snapshot as Record<string, unknown>
  if (typeof s.durationMinutes !== 'number' || typeof s.changesCount !== 'number') return null
  if (typeof s.computedAtIso !== 'string') return null
  if (!Array.isArray(s.lineIds) || !s.lineIds.every((id) => typeof id === 'string')) return null
  return {
    durationMinutes: s.durationMinutes,
    changesCount: s.changesCount,
    lineIds: s.lineIds,
    computedAtIso: s.computedAtIso,
  }
}

/**
 * A row written before `useCount` existed — or carrying a nonsense value —
 * reads as 1, never 0. Zero would rank a route the user has genuinely used
 * BELOW a brand-new one, silently inverting the ordering the field exists
 * to provide.
 */
function useCountFromRow(useCount: unknown): number {
  if (typeof useCount !== 'number' || !Number.isFinite(useCount)) return 1
  return Math.max(1, Math.floor(useCount))
}

export function loadHistory(): RouteHistoryEntry[] {
  return readRows<StoredHistoryRow>(HISTORY_KEY).flatMap((row) => {
    const key = keyFromRow(row)
    if (key === null || typeof row.lastUsedIso !== 'string') return []
    return [
      {
        key,
        snapshot: snapshotFromRow(row.snapshot),
        lastUsedIso: row.lastUsedIso,
        useCount: useCountFromRow(row.useCount),
      },
    ]
  })
}

export function saveHistory(entries: readonly RouteHistoryEntry[]): void {
  const rows: StoredHistoryRow[] = entries.map((entry) => ({
    id: canonicalId(entry.key),
    lastUsedIso: entry.lastUsedIso,
    useCount: entry.useCount,
    ...(entry.snapshot !== null ? { snapshot: entry.snapshot } : {}),
  }))
  writeRows(HISTORY_KEY, rows, 'routeHistory')
}

export function loadSavedRoutes(): SavedRoute[] {
  return readRows<StoredSavedRow>(SAVED_KEY).flatMap((row) => {
    const key = keyFromRow(row)
    if (key === null || typeof row.savedAtIso !== 'string') return []
    return [
      { key, label: typeof row.label === 'string' ? row.label : null, savedAtIso: row.savedAtIso },
    ]
  })
}

export function saveSavedRoutes(routes: readonly SavedRoute[]): void {
  const rows: StoredSavedRow[] = routes.map((route) => ({
    id: canonicalId(route.key),
    label: route.label,
    savedAtIso: route.savedAtIso,
  }))
  writeRows(SAVED_KEY, rows, 'savedRoutes')
}
